import { Component, forwardRef, Input, OnInit, StaticProvider } from "@angular/core";
import { AbstractControl, NG_VALUE_ACCESSOR, ValidatorFn, NG_VALIDATORS, FormBuilder, ValidationErrors } from "@angular/forms";
import { ControlBase } from ".";
import { UserHelperService } from "@main/users/user-helper.service";
import { Gender, PatientLogin, UserTitle } from "@main/users";
import { UserPatientValidators } from "./user-patient/validators";
import { Message } from "@bbwt/classes";
import { MessageService } from "primeng/api";

const EditPatientValueAccessor = <StaticProvider>{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => EditPatientControlComponent),
    multi: true
};

const EditPatientValidators = <StaticProvider>{
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => EditPatientControlComponent),
    multi: true
};

@Component({
    selector: "edit-patient-control",
    templateUrl: "./edit-patient-control.component.html",
    providers: [EditPatientValueAccessor, EditPatientValidators]
})
export class EditPatientControlComponent extends ControlBase<PatientLogin> implements OnInit {
    private _requiredEmail = true;
    private _selfRegister = false;
    isConnectedToNHS = false;
    dateOfBirthAsString: string = "";
    actualEmailAddress: string = "";

    protected patientForm = this.fb.group({
        id: [<string>null],
        patientId: [<string>null],
        miProNumber: [<string>null],
        title: [{ value: <UserTitle>null, disabled: this.isGenericPatient }, UserPatientValidators.title],
        firstName: [{ value: <string>null, disabled: this.isGenericPatient }, UserPatientValidators.firstName],
        lastName: [{ value: <string>null, disabled: this.isGenericPatient }, UserPatientValidators.lastName],
        dateOfBirth: [{ value: <Date>null, disabled: this.isGenericPatient }, UserPatientValidators.dateOfBirth],
        gender: [{ value: Gender.NotApplicable, disabled: this.isGenericPatient }, UserPatientValidators.gender],
        phoneNumber: [{ value: <string>null, disabled: this.isGenericPatient }, UserPatientValidators.phoneNumber],
        nhsNumber: [{ value: <string>null, disabled: true }],
        email: [{ value: <string>null, disabled: this.isGenericPatient }, UserPatientValidators.email(this.requiredEmail)],
        temporaryEmail:[<string>null],
        addressLine1: [{ value: <string>null, disabled: this.isGenericPatient }, UserPatientValidators.addressLine(this.selfRegister)],
        addressLine2: [{ value: <string>null, disabled: this.isGenericPatient }, UserPatientValidators.addressLine(this.selfRegister)],
        addressLine3: [{ value: <string>null, disabled: this.isGenericPatient }, UserPatientValidators.addressLine(false)],
        addressLine4: [{ value: <string>null, disabled: this.isGenericPatient }, UserPatientValidators.addressLine(false)],
        postcode: [{ value: <string>null, disabled: this.isGenericPatient }, UserPatientValidators.postcode(this.selfRegister)],
        /**
         * Leaving following controls/fields but please consider not adding new ones, instead try
         * to add in the form that uses this component
         */
        deliveryAddressLine1: [<string>null],
        deliveryAddressLine2: [<string>null],
        deliveryAddressLine3: [<string>null],
        deliveryAddressLine4: [<string>null],
        deliveryPostcode: [<string>null],
        createdDateTime: [<Date>null]
    });

    @Input() set requiredEmail(requiredEmail: boolean) {
        this._requiredEmail = requiredEmail;
        this.updateValidators();
    }
    get requiredEmail() {
        return this._requiredEmail;
    }
    @Input() showOtherInformation = true;
    @Input() set selfRegister(selfRegister: boolean) {
        this._selfRegister = selfRegister;
        this.updateValidators();
    }
    get selfRegister() {
        return this._selfRegister;
    }

    constructor(private fb: FormBuilder, private userHelperService: UserHelperService, private messageService: MessageService) {
        super();
    }

    ngOnInit(): void {
        this.patientForm.markAllAsTouched();
        this.patientForm.valueChanges.subscribe((value: PatientLogin) => this.onChange(value));
    }

    get isGenericPatient() {
        return this.userHelperService.isGenericPatient;
    }

    private updateValidators() {
        const refreshValidator = (c: AbstractControl, validator: ValidatorFn | ValidatorFn[]) => {
            if (c) {
                c.clearValidators();
                c.addValidators(validator);
                c.updateValueAndValidity({ onlySelf: true });
            }
        };

        refreshValidator(this.patientForm.get("email"), UserPatientValidators.email(this.requiredEmail));

        [this.patientForm.get("addressLine1"), this.patientForm.get("addressLine2")].forEach(c =>
            refreshValidator(c, UserPatientValidators.addressLine(this.selfRegister))
        );

        refreshValidator(this.patientForm.get("postcode"), UserPatientValidators.postcode(this.selfRegister));
    }

    writeValue(obj: PatientLogin): void {
        if(obj){
            if(obj.temporaryEmail != null && obj.temporaryEmail.length > 0 && obj.email != obj.temporaryEmail) {
                this.actualEmailAddress = obj.email;
                obj.email = obj.temporaryEmail
            }
        }

        this.patientForm.patchValue(obj);

        this.isConnectedToNHS = this.patientForm.get("nhsNumber").value !== null;

        if(this.isConnectedToNHS){
            const date = this.patientForm?.get("dateOfBirth")?.value;
            const day = String(date.getDate()).padStart(2, "0"); // Ensure two digits
            const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
            const year = date.getFullYear();
            this.dateOfBirthAsString = `${day}/${month}/${year}`;
        }
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if(this.patientForm.invalid){
            return {invalid: true}
        }
        return null;
    }

    showReadonlyFieldsWarning(){
        if(this.isConnectedToNHS){
            this.messageService.add(Message.Info("Unfortunately, you cannot edit this information. If you believe it is incorrect, please refer to the FAQ section for guidance."));
        }
    }
}
